import { useEffect, useState } from "react";

import type { StateOrName } from "@uirouter/react";

import type { RecentType } from "app/pages/digital-suite/home-page/components/RecentsWidget/types";
import type { RecentsTypes } from "app/services/solutions2Services/HomepageDataFetchers/HomepageDataFetcherTypes";
import {
    getRecentsAnalysis,
    getRecentsByParentState,
} from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";
import type { serializeItems } from "app/services/solutions2Services/HomepageDataFetchers/utils";

export type Recent = {
    id: string;
    data: {
        category?: string;
        country: number;
        pageTitle: string;
        forbidTrackerSuggestion: boolean;
        keyword?: string;
        params: {
            isWWW?: string;
            duration: string;
            isCountryChanged?: boolean;
            country: number;
            webSource: string;
            ignoreFilterConsistency?: boolean;
            isWebSourceChanged?: boolean;
            isDurationChanged?: boolean;
            key: string;
        };
        pageId: {
            section: string;
            subSection: string;
            subSubSection: string;
        };
        duration: string;
        visits: number;
        stateName: string;
        type: RecentType;
        mainItem: string;
        comparedItems: [];
        resources: {
            isCompare: false;
            comparedItemsFavicon: [];
            mainItemFavIcon: string;
        };
    };
    isFavorite: false;
    updatedTime: string;
    subtitle: string;
};

export interface IRecentsResult<T = Recent> {
    recents: Array<T>;
    isRecentLoading: boolean;
}

export const useRecents = <T = Recent>(
    filter?: RecentsTypes | RecentsTypes[],
    dedupe = false,
    dedupeFunction?: typeof serializeItems,
): IRecentsResult<T> => {
    const [recents, setRecents] = useState([]);
    const [loading, setLoading] = useState<boolean>(true);
    const filterDep = Array.isArray(filter) ? filter.join() : filter;

    useEffect(() => {
        const getRecents = async (): Promise<void> => {
            const recentItems = await getRecentsAnalysis(filter, dedupe, dedupeFunction);
            setRecents(recentItems ?? []);
            setLoading(false);
        };

        getRecents();
    }, [dedupe, filterDep]);

    return {
        recents,
        isRecentLoading: loading,
    };
};

export const useRecentsByParentState = (
    parentState: StateOrName | StateOrName[],
    dedupe = false,
    dedupeFunction?: typeof serializeItems,
): IRecentsResult => {
    const [recents, setRecents] = useState<Recent[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const filteredRecents = getRecentsByParentState(parentState, dedupe, dedupeFunction);

        setRecents(filteredRecents);
        setLoading(false);
    }, [parentState]);

    return {
        recents,
        isRecentLoading: loading,
    };
};
